import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { Chip, IconButton, Stack, SvgIcon, Tooltip, Typography, useTheme } from '@mui/material';
import Avatar from '@mui/material/Avatar';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import { User } from '../../../../app/services/api/generated';
import useNswagClient from '../../../../hooks/api/useNswagClient';
import useLogError from '../../../../hooks/useLogError';
import { Trash01 } from '../../../../assets';
import { ConfirmationDeleteDialog } from '../../../../components/shared/dialogs/ConfirmationDeleteDialog';
import MessagesSnackbar from '../../../../components/shared/MessagesSnackbar';
import { UserContext } from '../../../../components/shared/useUser';
import { TableColumn, TableSectionProps, TableSortDirection } from '../../../../components/shared/tables/TableSection';

type AccessLevelType = 'tenant' | 'franchise' | 'store' | 'none';

// Mirrors Orderly.Inventory.Common.Enums.UserStatusFilter's member names (culture-invariant .ToString() on the backend, not a translated display value).
const USER_STATUS = { Active: 'Active', Invited: 'Invited' } as const;

interface AccessInfo {
  level: AccessLevelType;
  scope: string;
  count: number;
}

const getAccessInfo = (user: User): AccessInfo => {
  if (user.tenants && user.tenants.length > 0) {
    const tenantNames = user.tenants.map(t => t.name).filter(Boolean).join(', ');
    return { level: 'tenant', scope: tenantNames || 'Unknown tenant', count: user.tenants.length };
  }

  if (user.franchises && user.franchises.length > 0) {
    const franchiseNames = user.franchises.map(f => f.franchiseName).filter(Boolean).join(', ');
    return { level: 'franchise', scope: franchiseNames || 'Unknown franchise', count: user.franchises.length };
  }

  if (user.stores && user.stores.length > 0) {
    const storeCount = user.stores.length;
    const storeNames = user.stores.slice(0, 3).map(s => s.storeName || s.storeNumber).join(', ');
    const scope = storeCount > 3 ? `${storeNames} +${storeCount - 3} more` : storeNames;
    return { level: 'store', scope: scope || 'Unknown stores', count: storeCount };
  }

  return { level: 'none', scope: 'No access configured', count: 0 };
};

interface UsersTableSectionProps {
  list: User[];
  totalCount: number;
  loadData: () => void;
  isLoading: boolean;
  page: number;
  maxPages: number;
  onPageChange: (page: number) => void;
  sortBy: string;
  sortDirection: TableSortDirection;
  onSortChange: (sortBy: string, sortDirection: TableSortDirection) => void;
}

export const useUsersTable = ({
  list, totalCount, loadData, isLoading, page, maxPages, onPageChange, sortBy, sortDirection, onSortChange,
}: UsersTableSectionProps): { table: TableSectionProps<User>; dialogs: React.ReactNode } => {
  const { t } = useTranslation('manageUsers');
  const { logError } = useLogError();
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { deleteUser, resendInvite } = useNswagClient();
  const { hasPermissionTo } = React.useContext(UserContext);

  const [selectedUser, setSelectedUser] = React.useState<User | undefined>(undefined);
  const [showDeleteModal, setShowDeleteModal] = React.useState(false);
  const [showDeleteSuccessMessage, setShowDeleteSuccessMessage] = React.useState(false);
  const [sendingInviteId, setSendingInviteId] = React.useState<number | undefined>(undefined);
  const [inviteSnackbar, setInviteSnackbar] = React.useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const handleResendInvite = (user: User) => {
    setSendingInviteId(user.id);
    resendInvite(user.id)
      .then(() => {
        setInviteSnackbar({ open: true, message: t('resendInviteSuccess', 'Invite sent.'), severity: 'success' });
      })
      .catch((error) => {
        logError(error);
        setInviteSnackbar({ open: true, message: t('resendInviteError', 'Failed to send invite.'), severity: 'error' });
      })
      .finally(() => setSendingInviteId(undefined));
  };

  const getAccessLevelColors = (level: AccessLevelType) => {
    switch (level) {
      case 'tenant':
        return { background: theme.palette.custom.violet[50], border: theme.palette.custom.violet[200], text: theme.palette.custom.violet[700] };
      case 'franchise':
        return { background: theme.palette.success[50], border: theme.palette.success[200], text: theme.palette.success[700] };
      case 'store':
        return { background: theme.palette.custom.blue[50], border: theme.palette.custom.blue[200], text: theme.palette.custom.blue[700] };
      case 'none':
      default:
        return { background: theme.palette.custom.gray[50], border: theme.palette.custom.gray[200], text: theme.palette.custom.gray[700] };
    }
  };

  const onDelete = (user: User) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  const handleDelete = () => {
    const id = selectedUser?.id;

    if (!id) {
      setShowDeleteModal(false);
      setSelectedUser(undefined);
      return;
    }

    deleteUser(id)
      .then(() => {
        setShowDeleteSuccessMessage(true);
        loadData();
      })
      .catch(logError);

    setShowDeleteModal(false);
    setSelectedUser(undefined);
  };

  const handleCloseModal = () => {
    setShowDeleteModal(false);
    setSelectedUser(undefined);
  };

  const columns: TableColumn<User>[] = [
    {
      id: 'emailAddress',
      label: t('email'),
      sortable: true,
      render: (row) => (
        <Typography variant='textXS'
          fontWeight={600}
          color={theme.palette.primary[500]}
          onClick={() => navigate(`/user-management/users/${row.id}${location.search}`, { state: { fromUser: row } })}
          sx={{ cursor: 'pointer', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
          {row.emailAddress}
        </Typography>
      ),
    },
    {
      id: 'persona',
      label: t('role'),
      sortable: true,
      render: (row) => <Typography variant='textXS'>{row.persona?.description}</Typography>,
    },
    {
      id: 'accessLevel',
      label: t('accessLevel'),
      render: (row) => {
        const accessInfo = getAccessInfo(row);
        const colors = getAccessLevelColors(accessInfo.level);
        const accessLevelLabel = {
          tenant: t('tenantLevel'),
          franchise: t('franchiseLevel'),
          store: t('storeLevel'),
          none: t('none'),
        }[accessInfo.level];

        return (
          <Chip
            label={accessLevelLabel}
            size="small"
            sx={{
              backgroundColor: colors.background,
              border: `1px solid ${colors.border}`,
              color: colors.text,
              fontWeight: 500,
              fontSize: '0.75rem',
              height: '24px',
            }}
          />
        );
      },
    },
    {
      id: 'accessScope',
      label: t('accessScope'),
      render: (row) => {
        const accessInfo = getAccessInfo(row);
        return (
          <Tooltip title={accessInfo.scope}
            arrow>
            <Typography
              variant='textXS'
              sx={{ maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}
            >
              {accessInfo.scope}
              {accessInfo.level === 'store' && accessInfo.count > 0 && (
                <Typography component="span"
                  variant='textXS'
                  color="text.secondary">
                  {` (${accessInfo.count} ${accessInfo.count === 1 ? 'store' : 'stores'})`}
                </Typography>
              )}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      id: 'lastLogin',
      label: t('lastLogin'),
      render: (row) => (
        <Typography variant='textXS'
          color="text.secondary">
          {row.lastLoginDate ? dayjs(row.lastLoginDate).format('DD/MM/YYYY HH:mm') : '—'}
        </Typography>
      ),
    },
    {
      id: 'status',
      label: t('status'),
      render: (row) => {
        if (row.status !== USER_STATUS.Active && row.status !== USER_STATUS.Invited) {
          return <Typography variant='textXS'
            color="text.secondary">—</Typography>;
        }

        const colors = row.status === USER_STATUS.Active
          ? { background: theme.palette.success[50], border: theme.palette.success[200], text: theme.palette.success[700] }
          : { background: theme.palette.custom.blue[50], border: theme.palette.custom.blue[200], text: theme.palette.custom.blue[700] };

        return (
          <Chip
            label={row.status === USER_STATUS.Active ? t('active') : t('invited')}
            size="small"
            sx={{
              backgroundColor: colors.background,
              border: `1px solid ${colors.border}`,
              color: colors.text,
              fontWeight: 500,
              fontSize: '0.75rem',
              height: '24px',
            }}
          />
        );
      },
    },
    {
      id: 'actions',
      label: '',
      align: 'right',
      render: (row) => (
        <>
          {(row.status === USER_STATUS.Invited && hasPermissionTo(['UserManagementResendInvite'])) && (
            <Tooltip title={t('resendInvite', 'Resend invite')}>
              <span>
                <IconButton aria-label="resend-invite"
                  disabled={sendingInviteId === row.id || !hasPermissionTo(['UserManagementResendInvite'])}
                  onClick={(event) => {
                    event.stopPropagation();
                    handleResendInvite(row);
                  }}>
                  <SvgIcon fontSize='small'>
                    <MailOutlineIcon />
                  </SvgIcon>
                </IconButton>
              </span>
            </Tooltip>
          )}
          <IconButton aria-label="delete"
            disabled={!hasPermissionTo(['UserManagementDelete'])}
            onClick={(event) => {
              event.stopPropagation();
              onDelete(row);
            }}>
            <SvgIcon fontSize='small'>
              <Trash01 color={theme.palette.error[500]} />
            </SvgIcon>
          </IconButton>
        </>
      ),
    },
  ];

  const table: TableSectionProps<User> = {
    title: t('users'),
    columns,
    rows: list,
    getRowId: (row) => row.id,
    recordCount: totalCount,
    recordCountLabel: t('users'),
    page,
    maxPages,
    onPageChange,
    sortBy,
    sortDirection,
    onSortChange,
    loading: isLoading,
    emptyMessage: t('noUsers'),
  };

  const dialogs = (
    <>
      <ConfirmationDeleteDialog
        open={showDeleteModal}
        onConfirm={handleDelete}
        onCancel={handleCloseModal}
        nameOfObject={t('user')}
      />
      <MessagesSnackbar
        open={showDeleteSuccessMessage}
        onClose={() => setShowDeleteSuccessMessage(false)}
        message={t('userDeleted')}
        severity="success"
        duration={null}
      />
      <MessagesSnackbar
        open={inviteSnackbar.open}
        onClose={() => setInviteSnackbar(prev => ({ ...prev, open: false }))}
        message={inviteSnackbar.message}
        severity={inviteSnackbar.severity}
        duration={4000}
      />
    </>
  );

  return { table, dialogs };
};
